import React, {FC, useMemo, useState} from 'react';
import {Bubble, List, Popover, Select} from 'tea-component';
import type { IColumnInfo, IColumnRawInfo, IOptionType } from '@dbl/core/dataSource';
import {
  DimNumberTrendDisplayValueList,
  DisplayValueList,
  MetricOptions,
  MetricTypes,
  SensitiveDisplayValueList,
} from '@dbl/core/dataSource';
import {
  TItemOnChange,
  STRING_OPTION_PAGE_SIZE,
} from '@/pages/datalab/components/SortableDrag/configration';
import { DateGranularity } from '@dbl/core/date';
import {stringType} from '@/pages/datalab/components/SortableDrag/typeDefine';
import {moment2StringEx, string2Moment} from '@/pages/datalab/components/SortableDrag/utils';
import {getLastDateRange, getStaticDateText, useDblIntl,} from '@/pages/datalab/utils/utils';
import {RangePicker} from '@/pages/home/components/RangePicker/RangePicker';
import {BMKAndDecimalComponent, EditList} from "@/pages/datalab/components/SortableDrag/FieldEditComponents";
import DynamicDate, {DateValue, DynamicDateValue} from "@/pages/datalab/components/DateComponents/DynamicDate";
import StaticDate from "@/pages/datalab/components/DateComponents/StaticDate";
import SvgIcon from "@home/components/SvgIcon";
import {Moment} from "moment";
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";
import StringSelectorWithTag from "@/pages/datalab/components/StringSelectorWithTag";
import {
  getDefaultStringOptionTimeFilter,
  useStringSelectorProps
} from "@/pages/datalab/components/StringSelectorWithTag/hooks";
import { getReportDimensionInfo } from '@dbl/services/filter';
import {DropdownIcon} from "@/pages/datalab/components/SortableDrag/Sortable/dropdownIcon";
import {shallowEqual, useDispatch, useIntl, useSelector} from "umi";
import { ChartType, type TChartType } from '@dbl/core/chart';
import { FieldType } from '@dbl/core/dataSource';
import { Operation } from '@dbl/core/filter';
import {
  NumberOperationSelect,
  NumberOperationValueInput,
  resolveValueOnOperationChange,
} from '@dbl/domainComponents/filter';
import {useUpdateEffect} from "ahooks";
import {LANGUAGE_EN} from '@home/_config/config';
import { ChartTypeDefaultUnit, FieldTypeDefaultAccuracy } from '@dbl/core/chartEditor';
import FieldTimeRange from "@/pages/datalab/components/StringSelectorWithTag/FieldTimeRange";
import {CheckOptionType} from "@/pages/datalab/components/FilterSelectorWithTag/type";
import {CHART_OLD_NAMESPACE} from "@/pages/datalab/models/namespaces";

/**
 * 日期列字段接口（与 FieldTimeRange 中的 IDateColumn 保持一致）
 */
interface IDateColumn {
  display_name?: string;
  display_name_en?: string;
  column?: string;
  name: string;
  type: string;
  chart_name_cn?: string;
  chart_name_en?: string;
  chart_id?: string;
  sql_expression?: string;
  id?: string;
  column_custom?: boolean;
  table_id?: string;
  table_code?: string;
}

// string 类型筛选器
export const StringSelect: FC<{
  item: IColumnInfo;
  itemOnChange: TItemOnChange;
  clickRename: Function;
  updateHandle: Function;
  dataTables: IGetTableInfoDto[];
  dataTableJoinRelations: ITableJoinRelation[];
  system: string;
}> = ({ item, itemOnChange, clickRename, updateHandle, dataTableJoinRelations, dataTables, system }) => {
  
  const {gameCode, customColumns} = useSelector((state: any) => ({
    gameCode: state?.datalab?.gameCode,
    customColumns: state?.['Analysis/MultiAnalysis']?.customColumns ?? []
  }), shallowEqual);
  
  const {params, dateColumns, defaultFieldFilter} = useMemo(() => {
    // 找到 当前字段(item)所在的数据表的 所有date类型的字段
    const dateColumns: IDateColumn[] = [];
    dataTables?.forEach(table => {
      // item是数据表字段
      const isOrigial = item.table_id && (item.table_id === table.table_id);
      // 或者 item是自定义字段, table_id没有值
      const isCustom = !item.table_id && !!item.sql_expression;
      if (isOrigial || isCustom) {
        // 先查找数据表中的日期字段
        if (Array.isArray(table?.column_list)) {
          // column.column_type 是 字段被用户转换后的类型
          // type是字段的原始类型
          table.column_list.forEach(column => {
            if ( (column.column_type ?? column.type) === 'date') {
              dateColumns.push({...column, table_id: table.table_id, table_code: item.table_code || 't0'});
            }
          });
        }
      }
    });
    
    // 再查找自定义字段的日期字段
    if (Array.isArray(customColumns)) {
      customColumns.forEach(column => {
        if (column.column_type === 'date') {
          dateColumns.push({
            display_name: column.name_cn,
            display_name_en: column.name_en,
            name: column.name_cn,
            type: column.column_type,
            sql_expression: column.sql_expression,
            id: column.id,
            column_custom: true,
            table_id: '',
            table_code: '',
          });
        }
      });
    }
    const defaultDateFilter = getDefaultStringOptionTimeFilter();
    // 请求字段的下拉选项的 过滤条件
    // 需要兼容过去旧数据 没有过滤条件的情况
    let defaultFilter;
    if (dateColumns[0]) {
      const field = dateColumns[0];
      // 先获取默认的日期过滤
      defaultFilter = {
        display_name: field.display_name,
        display_name_en: field.display_name_en,
        column: field.name,
        column_type: field.type,
        operation: 'recent',
        table_id: field.table_id ?? item.table_id,
        table_code: field.table_code ?? item.table_code,
        // 自定义字段
        sql_expression: field.sql_expression,
        ...defaultDateFilter
      };
    }
    
    const params = {
      dim_name: item.column_name,
      dimension_setting: item.dimension_setting,
      game_code: gameCode,
      has_next: true,
      page_no: 1,
      page_size: STRING_OPTION_PAGE_SIZE,
      table_id: item.table_id,
      system,
      table_code: item?.table_code,
      table_infos: dataTableJoinRelations,
      filter_key: item?.filterKey,
      // 如果有保存的 filters就用 item.filters, 没有就用默认的
      filters: Array.isArray(item.filters) ? item.filters : (defaultFilter ? [defaultFilter] : []),
    };
    
    if (item.column_custom) {
      // 自定义字段的请求下拉选项的传参
      // @ts-ignore
      params.custom_column = {
        custom_columns: [{
          name_cn: 'CustomField',
          column_type: 'float',
          name_en: 'CustomField',
          sql_expression: item.sql_expression,
          accuracy: 2,
        }],
        data_tables: dataTables,
        table_infos: dataTableJoinRelations,
        metrics: [
          {
            table_code: 't0',
            table_id: '',
            column: 'CustomField',
            label: 'CustomField',
            label_en: 'CustomField',
            column_type: 'string',
            aggregate: '',
            accuracy: 2,
            to_date_format: '',
          }
        ]
      };
      params.table_id = dataTables?.[0]?.table_id;
      
      // @ts-ignore
      params.expression = item.sql_expression;
    }
    return {params, dateColumns, defaultFieldFilter: defaultFilter};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTables, dataTableJoinRelations, customColumns]);
  const {
    loading, error, options, onSearchKeywordChange, getStringOptions, lastParams, keyword
  } = useStringSelectorProps({
    requestOptions: getReportDimensionInfo,
    params,
  });
  
  useUpdateEffect(() =>{
    // 自定义选项 item.sql_expression 有变更, 需要重新请求选项
    // SQL View的 sql语句有变更, 重新请求选项
    if (item.isAll) {
      itemOnChange({
        ...item,
        column_value: [],
      });
    }
    
    if (params.custom_column) {
      params.custom_column.custom_columns[0].sql_expression= item.sql_expression;
    }
    getStringOptions(params);
    
  }, [item?.sql_expression, dataTables?.[0]?.sql, item?.id]);
  // item?.sql_expression 是 自定义字段变更后, 重新拉取选项
  // dataTables?.[0]?.sql 是 SqlView编辑修改了sql之后, 重新拉取选项
  // item?.id 是数据源替换后, 字段被保留, id改变, 重新拉取选项
  
  return <StringSelectorWithTag
    boxClassName={'dbl-chart-string-selector'}
    options={options}
    loading={loading}
    error={error}
    operation={item.operation}
    onOperationChange={(operation) => {
      itemOnChange({
        ...item,
        operation,
      });
    }}
    isAllState={item.isAll}
    values={item?.column_value || []}
    onCheckChange={(value, newIsFull: boolean, customValues, context) => {
      itemOnChange({
        ...item,
        isAll: newIsFull,
        column_value: value,
        customValues,
      }, context === true);
      // context 在页面初始化的时候会传递true, 通知modal更新过滤条件的字段, 避免页面初始化后全选的筛选器, 下拉选项更新导致出现的黄色提示. context其他情况下传递的是选项上下文
    }}
    name={item.column_display}
    className={'dbl-filter-string-dropdown'}
    appearance={'default'}
    placementOffset={[-7, 5]}
    isExclude={item?.operation === Operation.Exclude}
    buttonRender={ (button) =>
      <div className="dt-multiple-dropdown-group">
        <div className="dt-multiple-dropdown-text">
          {button}
        </div>
        <DropdownIcon />
      </div>
    }
    onSearchKeywordChange={onSearchKeywordChange}
    topContentRender={ (close) => (<>
      {
        (item.is_dimension || item.isCommonDimension) ? (<><EditList
          key={item.id}
          options={[
            {type: 'rename', onClick: clickRename},
            {
              type: 'fieldType',
              system,
              value: item.column_type,
              decimal: item.accuracy,
              date_format: item.date_format,
              onChange: updateHandle
            },
          ]}
          close={close}
          column={item}
        />
        <div className={'dbl-split-line'}></div>
        </>) : ''
      }
      
      {
        defaultFieldFilter ? (<FieldTimeRange
          value={ item.filters?.[0] ?? defaultFieldFilter }
          onChange={filter => {
            const { lastCount, dateType } = filter;
            // lastCount === 'all_time' 说明用户选择了all time, 代表不筛选 选项
            const filters = [{
              ...filter,
              value: lastCount === 'all_time' ? [] : [`${lastCount}-${dateType}`],
              is_full: lastCount === 'all_time'
            }];
            // 发起选项请求
            getStringOptions({
              ...lastParams,
              keyword, // 可以兼容 前端搜索和后端搜索后, 切换日期范围后的请求参数中的keyword传参
              filters,
            });
            // 保存选项筛选
            itemOnChange({
              ...item,
              filters,
            });
          }}
          dateColumns={dateColumns}
        />) : ''
      }
    </>) }
    listHeight={210}
  />;
};
export const NumberSelect: FC<{ item: IColumnInfo; itemOnChange: TItemOnChange; clickRename?: Function; close: Function; updateHandle?: Function; system: string;}> = ({ item, itemOnChange, clickRename, close, updateHandle, system  }) => {
  const isPercent = item.column_type === 'percent';
  return (<div className="dtb-dl-ans-select-pop dropdown-card dbl-chart-number-filter">
    {
      (item.is_dimension || item.isCommonDimension) ? (<EditList
        key={item.id}
        options={[
          {type: 'rename', onClick: clickRename},
          {type: 'fieldType', system, value: item.column_type, decimal: item.accuracy, date_format: item.date_format, onChange: updateHandle},
        ]}
        close={close}
        column={item}
      />) : ''
    }
    <div className="card-content">
      <div className="card-content-select">
        <NumberOperationSelect
          className="card-content-select-input"
          operation={item?.operation}
          onChange={operation => {
            itemOnChange({
              ...item,
              operation,
              column_value: resolveValueOnOperationChange(
                operation,
                item?.column_value,
              ),
            });
          }}
        />
        <NumberOperationValueInput
          operation={item?.operation}
          columnValue={item?.column_value}
          onChange={columnValue => {
            itemOnChange({
              ...item,
              column_value: columnValue,
            });
          }}
          isPercent={isPercent}
        />
      </div>
    </div>
  </div>);
};

export const MetricsSelect: FC<{ item: IColumnInfo; itemOnChange: TItemOnChange; chartType: string; clickRename: Function; close: Function; updateHandle: Function; showCustomColumnModel: Function; dispatchFun: Function; dataTables: IGetTableInfoDto[]; dataTableJoinRelations: ITableJoinRelation[]; system: string; }> = ({ item, itemOnChange, chartType, clickRename, close, updateHandle, showCustomColumnModel, dispatchFun, dataTables, dataTableJoinRelations, system }) => {
  const {isEn} = useDblIntl( "sortable_drag");
  const select = item.aggregate;
  const isString = stringType.values.includes(item.column_type || '') || (item.column_type === 'date');
  // const isNumber = numberType.values.includes(item.column_type || '');
  
  const metricOptions: IOptionType[] = MetricOptions.map((option: IOptionType) => {
    let disableValue = false;
    if (item.column_sensitive) {
      disableValue = !SensitiveDisplayValueList.includes(option.value);
    } else {
      if (chartType === ChartType.DimNumberTrend){
        disableValue = DimNumberTrendDisplayValueList.includes(option.value);
      }
      if (isString) {
        disableValue = !disableValue ? DisplayValueList.includes(option.value) : disableValue;
      }
      if ((!item.column_custom) && MetricTypes.aggregated.value === option.value){
        // 不是自定义字段时, 下拉选的已聚合选项置灰
        disableValue = true;
      }
         
    }
    
    return {
      ...option,
      text: (isEn ? option.text_en : option.text) || '',
      disabled: disableValue,
    };
  });
  
  const disabledDisplay = !([FieldType.Number, FieldType.Percent, FieldType.Float].includes(item?.column_type) || ['distinct', 'count'].includes(item?.aggregate)) || [ChartType.PercentStackBar].includes(chartType);
  
  return (
    <>
      <EditList
        options={item?.column_custom ?
          [
            {type: 'custom', render: () => (<List.Item onClick={(e) => {
              e.stopPropagation();
              showCustomColumnModel(item);
              close();
            }
            }>{isEn ? 'Edit' : '编辑'}</List.Item>)},
            {type: 'fieldType', system, value: item.column_type, decimal: item.accuracy, chartType, onChange: (column: IColumnRawInfo, updateInfo: any) => {
              dispatchFun('updateCustomColumn',{
                updateColumn: {...updateInfo, id: item.id}
              });
        
            },
            customDateCheckParams: { // 用于自定义字段 转换日期格式的传参
              expression: item.sql_expression,
              table_id: dataTables[0]?.table_id || '',
              column: 'CustomField',
              custom_column: {
                custom_columns: [{
                  name_cn: 'CustomField',
                  column_type: 'float',
                  name_en: 'CustomField',
                  sql_expression: item.sql_expression,
                  accuracy: 2,
                }],
                data_tables: dataTables,
                table_infos: dataTableJoinRelations,
                metrics: [
                  {
                    table_code: 't0',
                    table_id: '',
                    column: 'CustomField',
                    label: 'CustomField',
                    label_en: 'CustomField',
                    column_type: 'string',
                    aggregate: '',
                    accuracy: 2,
                    to_date_format: '',
                  }
                ]
              }
            }
            },
          ] :
        
          [
            {type: 'rename', onClick: clickRename},
            {type: 'fieldType', value: item.column_type, decimal: item.accuracy, date_format: item.date_format, chartType, onChange: updateHandle},
          ]}
        close={close}
        column={item}
      />
      <List type="option" className={`dtb-dl-ans-select-pop metric-select-ul`}>
        {metricOptions.map((metricItem: IOptionType) => (
          <List.Item
            disabled={metricItem.disabled}
            key={metricItem.value}
            className={`${select === metricItem.value ? 'is-selected' : ''}`}
            onClick={() => {
              let accuracy: number | undefined = item?.accuracy ?? FieldTypeDefaultAccuracy[item.column_type];
              let unit: number | undefined = item?.unit ?? ChartTypeDefaultUnit[chartType as TChartType];
              if ([FieldType.String, FieldType.Date].includes(item.column_type) && (!['count', 'distinct'].includes(metricItem.value))) {
                accuracy = 0;
                unit = undefined;
              }
              
              itemOnChange({
                ...item,
                aggregate: metricItem.value,
                column_open: false,
                column_text: metricItem.text,
                accuracy,
                unit
              });
              close();
            }}
          >
            {metricItem.text}
          </List.Item>
        ))}
      </List>
      <List type="option" className={`dtb-dl-ans-select-pop bmk-ul`}>
        <BMKAndDecimalComponent
          onChange={item?.column_custom ? (column: IColumnRawInfo, updateInfo: any) => {
            dispatchFun('updateCustomColumn',{
              updateColumn: {...updateInfo, id: item.id, column_type: item.column_type}
            });
          } : updateHandle}
          accuracy={item.accuracy}
          unit={item.unit}
          column={item}
          disabled={disabledDisplay}
          hideUnit={item?.column_type === FieldType.Percent}
        />
      </List>
    </>
    
  );
};

// date day 类型筛选器
export const DaySelect: FC<{ label: React.ReactNode; item: IColumnInfo; itemOnChange: TItemOnChange; locale: string; clickRename: Function; }> = ({
  label,
  item,
  itemOnChange,
  locale,
  clickRename,
}) => {
  const onChange = (value: any) => {
    if (item.column_granularity === DateGranularity.MONTH && value.length === 2) {
      value[1] = value[1].endOf('month');
    }
    const newValue = [
      moment2StringEx(value[0], item.column_granularity),
      moment2StringEx(value[1], item.column_granularity),
    ];
    itemOnChange({
      ...item,
      column_value: newValue,
      column_open: false,
    });
  };
  const rangeType: string = useMemo(() => {
    const type = item.column_granularity.toLowerCase();
    return type === 'day' ? 'date' : type;
  }, [item.column_granularity]);

  const tempValue: [Moment, Moment] = useMemo(
    () => [string2Moment(item.column_value?.[0]), string2Moment(item.column_value?.[1])],
    [JSON.stringify(item.column_value)],
  );
  
  return (
    <RangePicker
      key="range-picker"
      type={rangeType}
      value={tempValue}
      defaultValue={tempValue}
      typeList={[rangeType]}
      startOfWeek={0}
      // language="cn"
      showSelectType={false}
      onChange={(value) => {
        onChange(value);
      }}
      label={label}
      language={locale === "en-US" ? "en" : "cn"}
      overlayClassName={'dbl_multi_date'}
      extraShortcut={{
        [rangeType]: [{
          name: 'Rename',
          nameCN: '重命名',
          onClick() {
            clickRename();
            return tempValue;
          }
        }]
      }}
    />
  );
};

export const DynamicStaticDateSelect: FC<{ item: IColumnInfo; itemOnChange: TItemOnChange; clickRename: Function; close: Function; updateHandle: Function; showCustomColumnModel: Function; isDisabledCompare: boolean;}> = ({ item, itemOnChange, clickRename, close, isDisabledCompare }) => {
  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [isDynamic, setIsDynamic] = useState(item?.operation === 'recent');
  const value = useMemo(() => {
    const { operation, column_value = [], column_text, dateType='day', lastCount=7, from, compareTimes, quickValue } = item;
    // dateType='day',
    const oldIsDynamic = operation === 'recent'; // 图表保存的是否是动态日期
    // const {isDynamic} = status; // 当前日期字段展示的面板中, 是否是动态日期
    const isSave = oldIsDynamic === isDynamic; // 判断面板和保存的是否一致

    // 转全小写匹配中英文翻译
    const newDateType = isSave ? dateType?.toLowerCase() : 'day';
    
    const newLastCount = isSave ? lastCount : 7;
    
    if (isDynamic) { // 动态日期
      let label = '';
      if (newLastCount == 0) { // 今年
        const suffix = formatMessage({id: `dbl_date_from`}, {from: formatMessage({id: `dbl_date_${newDateType}_ago_current`})}).toLowerCase();
        label = `: ${formatMessage({id: `dbl_date_year_prefix`})}${suffix}`;
      } else if (newLastCount == -1) { // 今天, 本周, 本月
        const suffix = formatMessage({id: `dbl_date_from`}, {from: formatMessage({id: `dbl_date_${newDateType}_ago_current`})}).toLowerCase();
        label = `: ${formatMessage({id: `dbl_date_${newDateType}_last_prefix_current`})}${suffix}`;
      } else if (newLastCount == -2) { // 今天至今
        
        label = `: ${formatMessage({id: `dbl_date_hour_now`})}`;
      }
      else {
        let prefix = '';
        if (newLastCount === '1') {
          prefix = formatMessage({id: `dbl_date_${newDateType}_last_prefix_1`});
        } else if (newLastCount === 1) {
          prefix = newDateType === 'day' ? formatMessage({id: `dbl_date_${newDateType}_last_1`}) : formatMessage({id: `dbl_date_${newDateType}_last_prefix_1`});
        } else {
          prefix = formatMessage({id: `dbl_date_${newDateType}_last_prefix`}, {last: newLastCount});
        }

        // 计算文本 后缀
        const fromValue = from || '1';
        let fromText = '';
        if (fromValue == '-1') {  // 今天, 本周, 本月
          fromText = formatMessage({id: `dbl_date_${newDateType}_ago_current`});
        } else if (fromValue == '1') { // 昨天, 上周, 上月
          fromText = formatMessage({id: `dbl_date_${newDateType}_ago_1`});
        } else {
          fromText = formatMessage({id: `dbl_date_${newDateType}_ago`}, {ago: fromValue});
        }
        const suffix = formatMessage({id: `dbl_date_from`}, {from: fromText}).toLowerCase();

        label = `: ${prefix}${suffix}`;
      }

      return {
        isDynamic,
        dateType: newDateType,
        lastCount: newLastCount,
        label,
        from: from || '1',
        dateRange: isSave ? (column_text?.split('~') || []) : getLastDateRange(7, 'day'),
        compareTimes: isSave ? compareTimes : [],
      };
      
    }
    
    // 静态日期
    const columnValue = isSave ? column_value : getLastDateRange(7, 'day');
    const text = column_text || getStaticDateText(columnValue as [string, string], dateType);
    return {
      isDynamic,
      dateRange: columnValue,
      dateType: newDateType,
      label: `: ${text}`,
      from: undefined,
      compareTimes: isSave ? compareTimes : [],
      quickValue,
    };
    
  }, [item, isDynamic]);
  return (<>
    {
      item?.column_custom ? '' : (<EditList
        options={[
          {type: 'rename', onClick: clickRename},
        ]}
        close={close}
        column={item}
      />)
    }
    
    <List type="option" className={`dtb-dl-ans-select-pop  ${item?.column_custom ? '' : 'dbl-filter-date-ul'}`}>
      <Popover
        overlay={(
          <DynamicDate
            isEn={isEn}
            fieldName={item?.column_display || item?.column_name}
            defaultValue={value}
            onChange={({dateRange, dateType, lastCount, from, compareTimes, quickValue }: DynamicDateValue) => {
              const columnText = getStaticDateText(dateRange, dateType);
              itemOnChange({
                ...item,
                id: item.id,
                table_code: item.table_code,
                operation: 'recent',
                column_text: columnText,
                column_value: [`${lastCount}-${dateType}`],
                lastCount,
                dateType,
                from,
                compareTimes,
                quickValue,
                // dateRange,
              });
            }}
            onClose={close}
            isDisabledCompare={isDisabledCompare}
            getOverlayPositionConfig={() => ({
              overlayEle: document.querySelector('.dbl-dynamic-date-overlay'),
              labelEle: document.querySelector('.dbl-dynamic-date-label'),
              placement: 'right-start',
              offset: { mainAxis: 15, crossAxis: 0 },
            })}
          />
        )}
        placement={'right-start'}
        trigger={'click'}
        overlayClassName={'dbl-dynamic-date-overlay'}
        placementOffset={[0, 15]}
      >
        <List.SubMenu
          className={'dbl-dynamic-date-label'}
          selected={isDynamic}
          label={`${formatMessage({id: 'dbl_common_dynamic_time'})} ${value.isDynamic ? value.label : ''}`}
          onClick={() => {
            if (isDynamic) return;
            setIsDynamic(true);
          }}
        />
      </Popover>
      
      <StaticDate
        isEn={isEn}
        placementOffset={[-12, 8]}
        value={value}
        close={close}
        onChange={({dateRange, dateType, compareTimes, quickValue}: DateValue) => {
          itemOnChange({
            ...item,
            id: item.id,
            table_code: item.table_code,
            operation: 'between',
            column_granularity: dateType,
            column_text: getStaticDateText(dateRange, dateType),
            column_value: dateRange,
            dateType,
            from: undefined,
            compareTimes,
            quickValue,
          });
        }}
        isDisabledCompare={isDisabledCompare}
        getOverlayPositionConfig={() => ({
          overlayEle: document.querySelector('.dbl_multi_date.dbl-static-date'),
          labelEle: document.querySelector('.dbl-static-date-label'),
        })}
      >
        <List.SubMenu
          selected={!isDynamic}
          className={'dbl-static-date-label'}
          label={(<>
            {formatMessage({id: 'dbl_common_static_time'})}
            <Bubble
              dark
              content={formatMessage({id: 'dbl_common_static_time_info'})}
            >
              <span style={{marginLeft: '4px'}}> <SvgIcon type={'alert-circle-action'}/></span>
            </Bubble>
            &nbsp;
            {value.isDynamic ? '' : value.label}
          </>)}
          onClick={() => {
            if (!isDynamic) return;
            setIsDynamic(false);
          }}
        />
        
      </StaticDate>
      
    </List>
  </>);
  
};